import { ActionContext, ActionTree, createStore, mapMutations, mapState, Module } from 'vuex'

import graph from './modules/graph'
import properties from './modules/properties'
import monitoring from './modules/monitoring'
import rules from './modules/rules'
import bindings from './modules/bindings'
import schema from './modules/schema'
import lists from './modules/lists'
import { errorHandler } from './plugins/errorHandler'
import auth, { User } from '@/api/auth'
import system from '@/api/system'
import { AxiosError } from 'axios'

const state = () => ({
	hidePanel: false,
	showErrorPopup: true,
	errors: [] as ErrorData[],
	user: {} as User,
	version: {
		backend: undefined as string | undefined,
		frontend: APP_VERSION
	},
	locale: undefined as string | undefined
})

export type RootState = ReturnType<typeof state>

export function mapRootState<K extends keyof RootState & string> (keys: K[]) {
	return mapState(keys) as { [P in K]: () => RootState[P] }
}

interface Error {
	type: string
	code?: string
	message: string
	cause?: Error
}

export interface ErrorData {
	type?: string
	code?: string
	message: string
}

const extractErrorData = function efd(error: Error): ErrorData[] {
	const { type, code, message, cause } = error
	if (cause) {
		return [ { type, code, message }, ...efd(cause) ]
	} else {
		return [ { type, code, message } ]
	}
}

const mutations = {
	SET_LOCALE (state: RootState, locale: string) {
		state.locale = locale
	},

	SET_VERSION (state: RootState, version: string) {
		state.version.backend = version
	},

	SET_USER (state: RootState, user: User) {
		state.user = user
	},

	ADD_ERROR (state: RootState, error: any) {
		console.error(error)
		if (error instanceof AxiosError) {
			const data: Error | undefined = error.response?.data
			const errorData = extractErrorData(data ?? { type: 'AxiosError', code: error.code, message: error.message })
			errorData.forEach(e => state.errors.push(e))
		} else {
			state.errors.push({ message: error.toString() })
		}
		state.showErrorPopup = true
	},

	FLUSH_ERROR (state: RootState, index: number) {
		state.errors.splice(index, 1)
	},

	HIDE_ERROR_POPUP (state: RootState) {
		state.showErrorPopup = false
	},

	TOGGLE_PANEL (state: RootState) {
		state.hidePanel = !state.hidePanel
	}
}

type RootMutations = typeof mutations
type MappedMutation<M> = M extends (state: any, ...payload: infer P) => infer R
	? (...payload: P) => R
	: never

type TypedCommit = {
	<K extends keyof RootMutations & string> (
		type: K,
		...payload: Parameters<MappedMutation<RootMutations[K]>>
	): void
}

type RootActionContext = Omit<ActionContext<RootState, RootState>, 'commit'> & {
	commit: TypedCommit
}

export function mapRootMutations<K extends keyof RootMutations & string> (keys: K[]) {
	return mapMutations(keys) as { [P in K]: MappedMutation<RootMutations[P]> }
}

const actions = {
	async fetchVersion ({ commit }: RootActionContext) {
		const response = await system.version()
		commit('SET_VERSION', response.data.version)
	},

	async logIn ({ dispatch, commit }: RootActionContext, { login, password }) {
		try {
			await auth.logIn({ login, password })
			await dispatch('fetchUser')
		} catch (error) {
			commit('ADD_ERROR', error)
		}
	},

	async logOut ({ dispatch, commit }: RootActionContext) {
		try {
			await auth.logOut()
			await dispatch('fetchUser')
		} catch (error) {
			commit('ADD_ERROR', error)
		}
	},

	async fetchUser ({ commit }: RootActionContext) {
		try {
			const response = await auth.get()
			commit('SET_USER', response.data)
		} catch (error) {
			commit('ADD_ERROR', error)
		}
	}
} as ActionTree<RootState, RootState>

const modules: Record<string, Module<any, any>> = {
	graph,
	properties,
	monitoring,
	rules,
	bindings,
	schema,
	lists
}

export default createStore<RootState>({
	state,
	getters: {
		authorized (state) {
			return state.user.role !== 'unauthorized'
		}
	},
	actions,
	mutations,
	strict: process.env.NODE_ENV !== 'production',
	modules,
	plugins: [ errorHandler ]
})
